import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Snackbar,
} from '@mui/material';
import { Build as BuildIcon } from '@mui/icons-material';
import { adminApi } from '../../api';
import type { BackfillConnectionsResult } from '../../api/admin';
import { useAuth } from '../../hooks/useAuth';
import { useDocumentTitle } from '../../hooks/core';
import { isAdmin } from '../../types';

interface TaskCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  loading: boolean;
  error: string | null;
  result: React.ReactNode | null;
  buttonLabel: string;
  loadingLabel: string;
  onRun: () => void;
}

function TaskCard({
  title,
  description,
  icon,
  color,
  loading,
  error,
  result,
  buttonLabel,
  loadingLabel,
  onRun,
}: TaskCardProps) {
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: color,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {description}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {result}

            <Button
              variant="contained"
              color="warning"
              onClick={onRun}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : icon}
            >
              {loading ? loadingLabel : buttonLabel}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export function AdminTasksPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useDocumentTitle('Admin Tasks');

  // Backfill connections state
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillResult, setBackfillResult] = useState<BackfillConnectionsResult | null>(null);
  const [backfillError, setBackfillError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Redirect non-admins
  React.useEffect(() => {
    if (!isAdmin(user)) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleBackfillConnections = async () => {
    setBackfillLoading(true);
    setBackfillError(null);
    setBackfillResult(null);

    try {
      const result = await adminApi.backfillPlayerConnections();
      setBackfillResult(result);
      setSnackbarMessage('Player connections backfill completed successfully');
      setSnackbarOpen(true);
    } catch (err) {
      setBackfillError(err instanceof Error ? err.message : 'Failed to run backfill');
    } finally {
      setBackfillLoading(false);
    }
  };

  const renderBackfillResult = () => {
    if (!backfillResult) return null;

    return (
      <Alert severity="success" sx={{ mb: 2 }}>
        Backfill complete: {backfillResult.onlineConnectionsCreated} online connections,{' '}
        {backfillResult.offlineConnectionsCreated} offline connections created for{' '}
        {backfillResult.usersProcessed} users.
        {backfillResult.errors.length > 0 && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Errors: {backfillResult.errors.join(', ')}
          </Typography>
        )}
      </Alert>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        System Tasks
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Administrative tasks for data management and maintenance. These tasks should be run
        carefully as they may affect multiple records in the database.
      </Typography>

      <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
        Data Migration
      </Typography>

      <TaskCard
        title="Backfill Player Connections"
        description="Create player connection records for existing users based on their shared board history. This is a one-time migration task for legacy data. Safe to run multiple times - it will skip already existing connections."
        icon={<BuildIcon />}
        color="#ff9800"
        loading={backfillLoading}
        error={backfillError}
        result={renderBackfillResult()}
        buttonLabel="Run Backfill"
        loadingLabel="Running Backfill..."
        onRun={handleBackfillConnections}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  );
}
